import React, { useEffect, useState, useCallback } from 'react';
import { ArrowUpDown, AlertTriangle } from 'lucide-react';
import { META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';
import { fmtNum, fmtSpend, fmtPct } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SORT_OPTIONS = [
  { value: 'spend',            label: 'Spend' },
  { value: 'clicks',           label: 'Clicks' },
  { value: 'kop',              label: 'Köp' },
  { value: 'kostnad_per_kop',  label: 'Kostnad/köp' },
];

function Sparkline({ values }) {
  if (!values || values.length < 2) {
    return <span className="mstat-sparkline-empty" title="Behöver minst 2 dagars historik">—</span>;
  }
  const w = 84, h = 26, pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = (w - pad * 2) / (values.length - 1);
  const points = values.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const pointsStr = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const trendUp = values[values.length - 1] >= values[0];
  const color = trendUp ? '#22c55e' : '#dc2626';
  const gradId = `mstat-spark-grad-${trendUp ? 'up' : 'down'}`;
  const areaPath = `M${points[0][0].toFixed(1)},${h - pad} ` +
    points.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(' ') +
    ` L${points[points.length - 1][0].toFixed(1)},${h - pad} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mstat-sparkline" data-testid="metastats-campaign-sparkline">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
      <polyline points={pointsStr} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2" fill={color} />
    </svg>
  );
}

export default function MetaStatsCampaignsTab({ accessToken, marketsParam }) {
  const [sortBy, setSortBy] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [marketFilter, setMarketFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/meta-stats/campaigns?markets=${marketsParam}&sort_by=${sortBy}&sort_dir=${sortDir}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta kampanjdata.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (val) => {
    if (sortBy === val) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(val); setSortDir('desc'); }
  };

  const marketsInData = data ? Array.from(new Set(data.campaigns.map(c => c.market))) : [];
  const rows = data ? data.campaigns.filter(c => marketFilter === 'all' || c.market === marketFilter) : [];
  const GRID_COLS = '0.6fr 1.4fr 0.9fr 0.9fr 0.8fr 0.7fr 0.7fr 0.7fr 0.9fr';

  return (
    <div data-testid="metastats-campaigns-tab">
      <div className="mstat-tab-badge-row"><MetaChannelBadge type="paid" /></div>
      <div className="tab-switcher" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button
          data-testid="metastats-campaign-filter-all"
          className={`tab-btn${marketFilter === 'all' ? ' tab-btn--active' : ''}`}
          style={{ fontSize: 12 }}
          onClick={() => setMarketFilter('all')}
        >
          Alla marknader
        </button>
        {marketsInData.map(m => (
          <button
            key={m}
            data-testid={`metastats-campaign-filter-${m}`}
            className={`tab-btn${marketFilter === m ? ' tab-btn--active' : ''}`}
            style={{ fontSize: 12 }}
            onClick={() => setMarketFilter(m)}
          >
            {META_MARKET_FLAGS[m] || ''} {m}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>Sortera efter:</span>
        {SORT_OPTIONS.map(o => (
          <button
            key={o.value}
            data-testid={`metastats-campaign-sort-${o.value}`}
            className={`tab-btn${sortBy === o.value ? ' tab-btn--active' : ''}`}
            style={{ fontSize: 12 }}
            onClick={() => toggleSort(o.value)}
          >
            {o.label} <ArrowUpDown size={11} style={{ marginLeft: 4, verticalAlign: '-1px' }} />
          </button>
        ))}
      </div>

      {error && <div className="admin-notice" data-testid="metastats-campaigns-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Laddar kampanjer...</div>
      ) : rows.length === 0 ? (
        <div className="admin-empty" data-testid="metastats-campaigns-empty">Ingen kampanjdata för valda marknader.</div>
      ) : (
        <div className="admin-table" data-testid="metastats-campaigns-table">
          <div className="admin-table-header" style={{ gridTemplateColumns: GRID_COLS }}>
            <span>Marknad</span><span>Kampanj</span><span>Spend</span><span>Impressions</span>
            <span>Trend</span><span>Clicks</span><span>CTR</span><span>Köp</span><span>Kostnad/köp</span>
          </div>
          {rows.map(c => (
            <div
              key={c.id}
              className="admin-table-row"
              style={{ gridTemplateColumns: GRID_COLS }}
              data-testid={`metastats-campaign-row-${c.id}`}
            >
              <span>{META_MARKET_FLAGS[c.market] || ''} {c.market}</span>
              <span title={c.campaign_name}>
                {c.campaign_name}
                {c.fel_not && (
                  <span title={c.fel_not} style={{ marginLeft: 6, color: '#f59e0b' }}>
                    <AlertTriangle size={12} style={{ verticalAlign: '-2px' }} />
                  </span>
                )}
              </span>
              <span>{fmtSpend(c.spend)}</span>
              <span>{fmtNum(c.impressions)}</span>
              <span><Sparkline values={c.spend_history} /></span>
              <span>{fmtNum(c.clicks)}</span>
              <span>{fmtPct(c.ctr)}</span>
              <span>{fmtNum(c.kop)}</span>
              <span>
                <span
                  className={`mstat-cost-badge${c.is_high_cost ? ' mstat-cost-badge--high' : ''}`}
                  data-testid={c.is_high_cost ? `metastats-campaign-highcost-${c.id}` : undefined}
                >
                  {c.is_high_cost && <AlertTriangle size={11} />}
                  {fmtSpend(c.kostnad_per_kop)}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
